import { Injectable } from '@angular/core';
import startCase from 'lodash-es/startCase';
import mixpanel from 'mixpanel-browser';
import { Profile } from 'src/app/models/profile.model';
import { environment } from 'src/environments/environment';
import { KeyValue, MixpanelOptions, MixpanelPayload, NavPayload, VerifyLogin } from '../../interfaces';
import { GROUPNAME, TRACK } from './mixpanel.event';
import { Device } from '@ionic-native/device/ngx';
import { Platform } from '@ionic/angular';

@Injectable({ providedIn: 'root' })
export class MixpanelAnalyticsService {
  private _enabled = false;
  private _optedOut = false;
  private _currentIdentity: string;
  private _excludedUrlPatterns: Array<RegExp> = [];
  private _pageData: NavPayload = { pageAction: 'Root Page Visit', path: '/' };
  private _defaultOptions: MixpanelOptions = { pageData: true };
  private _whiteListMixpanelProps = ['communication_preferences', 'id', 'pin_code_enabled', 'pin_code_status'];
  private _blackListMixpanelProps = ['name', 'first_name', 'email', 'last_name', 'mobile', 'dob'];

  handleNestedObject = obj => {
    for (const property in obj) {
      if (typeof obj[property] === 'object' && !Array.isArray(obj[property])) delete obj[property];
    }
  };

  get canTrack() {
    return this._enabled && !this._optedOut;
  }

  track(eventName: TRACK | string, payload: MixpanelPayload = {}, options?: MixpanelOptions) {
    if (this.canTrack) {
      const filteredList = { ...payload };
      this._blackListMixpanelProps.forEach(property => {
        if (filteredList[property]) delete filteredList[property];
      });
      this.handleNestedObject(filteredList);
      options = { ...this._defaultOptions, ...options };
      payload = { ...filteredList, ...(options.pageData ? this._pageData : {}) };
      mixpanel.track(eventName, payload);
    }
  }

  async login(profile: Partial<Profile>) {
    const { id, email, name, preferred_language, organisation } = profile;
    if (this.canTrack && !this._currentIdentity) {
      this._currentIdentity = `${id}`;
      mixpanel.identify(this._currentIdentity);
      const language = preferred_language || organisation.default_language;
      const isTestUser =
        (email &&
          ['@weguide.com.au', '@weguide.com', '@weguide.health', '@curvetomorrow.com', '@curvetomorrow.com.au', '+test', '+testing'].some(
            v => email.includes(v)
          )) ||
        (name && ['test', 'testing', 'weguide'].some(v => name.includes(v)));
      this.setSuperPropertiesOnce({ id: this._currentIdentity, ...(isTestUser ? { isTestUser } : {}) });
      const whiteListed = {};
      this._whiteListMixpanelProps.forEach(property => {
        if (profile[property]) whiteListed[property] = profile[property];
      });
      this.handleNestedObject(whiteListed);
      this.setUserProperties({ ...whiteListed, languages: language?.locale });
      if (isTestUser) this.setUserPropertiesOnce({ isTestUser });
      if (organisation.tenant_name) {
        this.addToGroup(GROUPNAME.ORGANIZATIONS, organisation.tenant_name);
      }
      if (language) {
        this.addToGroup(GROUPNAME.LANGUAGES, language.locale);
      }
    }
  }

  reset() {
    if (this.canTrack) {
      setTimeout(() => {
        mixpanel.reset();
      }, 300);
    }
  }

  updateAndTrack(prop: string, value: string, groupName?: GROUPNAME) {
    if (this.canTrack) {
      this.track(startCase(`${prop} updated`), { [prop]: value });
      this.setUserProperties({ [prop]: value });
      if (groupName) this.addToGroup(groupName, value);
    }
  }

  appendAndUpdate(prop: string, value: string, groupName?: GROUPNAME) {
    if (this.canTrack) {
      this.track(startCase(`${prop} updated`), { [prop]: value });
      this.appendUserProperties({ [prop]: value });
      if (groupName) this.addToGroup(groupName, value);
    }
  }

  addToGroup(groupName: GROUPNAME, value: string) {
    if (this.canTrack && value) {
      mixpanel.add_group(groupName, value);
    }
  }

  verifyLogin({ schema, patient_id }: VerifyLogin) {
    if (this.canTrack && !this._currentIdentity) {
      if (patient_id) {
        mixpanel.identify(patient_id);
      }
      if (schema) {
        this.setSuperPropertiesOnce({ schema });
        this.setUserProperties({ schema });
      }
    }
  }

  setUserProperties(properties: KeyValue) {
    const updatedProps = this.checkPropertiesForComPrefrence(properties);
    if (this.canTrack) {
      mixpanel.people.set(properties);
    }
  }

  appendUserProperties(properties: KeyValue) {
    if (this.canTrack) {
      mixpanel.people.append(properties);
    }
  }

  setUserPropertiesOnce(properties: KeyValue) {
    const deviceProps = this.getDeviceInfo();
    if (this.canTrack) mixpanel.people.set_once({ ...properties, ...deviceProps });
  }

  setSuperProperties(properties: KeyValue) {
    const deviceProps = this.getDeviceInfo();
    if (this.canTrack) mixpanel.register({ ...properties, ...deviceProps });
  }

  setSuperPropertiesOnce(properties: KeyValue) {
    if (this.canTrack) mixpanel.register_once(properties);
  }

  trackUrl(urlPath: string) {
    const canTrack = this._excludedUrlPatterns.every((pattern: RegExp) => !pattern.test(urlPath));
    const [pathWithoutParams, _] = urlPath.split('?');
    const path = pathWithoutParams.replace(/token.*/, 'token');
    const pageParts = pathWithoutParams.split('/');
    const pageAction = path.includes('token')
      ? 'App Opened'
      : startCase(`${pageParts[1] !== 'tabs' ? pageParts[1] || 'root' : pageParts[2] || pageParts[1]} Page Visit`);
    this._pageData = { pageAction, path };
    if (canTrack) {
      this.track(this._pageData.pageAction, { tracking: 'auto' });
    }
  }

  checkPropertiesForComPrefrence(properties: KeyValue): KeyValue {
    const updatedProps = properties;
    if (updatedProps.communication_preferences) {
      const commPrefs = (updatedProps.communication_preferences as string[]).filter(p => p.length > 1);
      if (commPrefs.length < 1) {
        updatedProps.communication_preferences = ['Not_received_from_system'];
      }
    }
    return updatedProps;
  }

  constructor(private device: Device, public platform: Platform) {
    this._enabled = !!(environment as any).firebaseConfig.mixpanelProjectToken;
    // Reason: environment cant be mocked since it is hardboud to webpack
    /* istanbul ignore next */
    if (this._enabled) {
      mixpanel.init((environment as any).firebaseConfig.mixpanelProjectToken, { property_blacklist: ['$current_url'], ip: false });
      this._optedOut = mixpanel.has_opted_out_tracking();
    }
  }

  getDeviceInfo() {
    let deviceProps = {};
    if (!this.platform.is('cordova')) {
      deviceProps = {
        applicationType: 'PWA',
      };
      return deviceProps;
    }
    let userApplicationType = 'Android';
    if (this.platform.is('ios')) {
      userApplicationType = 'iOS';
    }
    deviceProps = {
      userDevicemanufacturer: this.device.manufacturer,
      applicationType: userApplicationType,
      userDevicemodel: this.device.model,
      userDeviceversion: this.device.version,
    };
    return deviceProps;
  }
}
